import React, { useEffect } from "react";
import HomeIcon from "@mui/icons-material/Home";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PhotoIcon from "@mui/icons-material/Photo";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SubjectIcon from "@mui/icons-material/Subject";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Card,
  Typography,
  Container,
  Box,
  Table,
  TableBody,
  TableCell,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableContainer,
  Dialog,
  TableRow,
  Paper,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Avatar,
  Chip,
  Grid,
  Tooltip,
  Stack,
  CircularProgress,
} from "@mui/material";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import VisitorApprovalVM from "../viewmodal/VisitorApprovalVM";

const VisitorApprovalView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const {
    visitorData,
    status,
    loading,
    actionLoading,
    disabled,
    openPhotoPreview,
    openDocumentPreview,
    photoPreview,
    documentPreview,
    showActionMessage,
    isFinalStatus,
    showButtons,
    handleHomeClick,
    handleOpenPhotoPreview,
    handleClosePhotoPreview,
    handleOpenDocumentPreview,
    handleCloseDocumentPreview,
    handleApprove,
    handleReject,
    handleOnHold,
    formatDate,
  } = VisitorApprovalVM();

  // Security measures to prevent right-click and keyboard shortcuts
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable F12 (Developer Tools)
      // if (e.key === 'F12') {
      //   e.preventDefault();
      //   return false;
      // }
      
      // Disable Ctrl+Shift+I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+U (View Source)
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+C (Element Inspector)
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+S (Save Page)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+A (Select All)
      if (e.ctrlKey && e.key === 'a') {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);

    // Cleanup event listeners
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  // Show main loading backdrop when fetching visitor data
  if (loading) return <CBackDrop cOpen={loading} />;

  // Guard against null visitorData
  if (!visitorData) {
    return (
      <Box 
        sx={{ 
          backgroundColor: "#f5f7fa",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        <Container maxWidth="sm">
          <Card
            elevation={3}
            sx={{
              padding: { xs: "1.5em", sm: "2em" },
              borderRadius: "12px",
              backgroundColor: "#fff",
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            <Avatar sx={{ 
              width: 60, 
              height: 60, 
              bgcolor: "#f44336", 
              margin: "0 auto 1em",
            }}>
              <CancelIcon fontSize="large" />
            </Avatar>
            <Typography variant="h5" gutterBottom fontWeight="600">
              Data Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Visitor data could not be loaded or doesn't exist
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleHomeClick} 
              startIcon={<HomeIcon />}
              size="large"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              Back to Dashboard
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  // Status badge component with icons
  const StatusBadge = ({ status }) => {
    let color, backgroundColor, icon;

    switch (status) {
      case "Approved":
        color = "#1b5e20";
        backgroundColor = "#e8f5e9";
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case "Rejected":
        color = "#b71c1c";
        backgroundColor = "#ffebee";
        icon = <CancelIcon fontSize="small" />;
        break;
      case "On Hold":
        color = "#e65100";
        backgroundColor = "#fff3e0";
        icon = <HourglassEmptyIcon fontSize="small" />;
        break;
      case "Pending":
        color = "#0d47a1";
        backgroundColor = "#e3f2fd";
        icon = <HourglassEmptyIcon fontSize="small" />;
        break;
      default:
        color = "#424242";
        backgroundColor = "#f5f5f5";
        icon = <HourglassEmptyIcon fontSize="small" />;
    }

    return (
      <Chip
        icon={icon}
        label={status}
        sx={{
          backgroundColor: backgroundColor,
          color: color,
          fontWeight: 600,
          px: 1,
          "& .MuiChip-icon": {
            color: color,
          },
        }}
      />
    );
  };

  // Create data rows for responsiveness
  const visitorDataRows = [
    {
      label: "Visitor Name",
      value: visitorData.full_name,
      icon: <PersonIcon color="primary" />,
    },
    {
      label: "Visitor Mobile",
      value: visitorData.contact_number,
      icon: <PhoneIcon color="primary" />,
    },
    {
      label: "Visitor Email",
      value: visitorData.email,
      icon: <EmailIcon color="primary" />,
    },
    {
      label: "Visit From",
      value: formatDate(visitorData.visit_from),
      icon: <CalendarTodayIcon color="primary" />,
    },
    {
      label: "Visit Till",
      value: formatDate(visitorData.visit_till),
      icon: <CalendarTodayIcon color="primary" />,
    },
    {
      label: "Meeting Purpose",
      value: visitorData.purpose,
      icon: <SubjectIcon color="primary" />,
    },
    {
      label: "Whom to Visit",
      value: visitorData.whom_to_visit,
      icon: <PersonIcon color="primary" />,
    },
  ];

  return (
    <Box 
      sx={{ 
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        py: { xs: 1, sm: 4, md: 6 },
        px: { xs: 1, sm: 3, md: 4 },
        mt: { xs: 8, sm: 4, md: 5 },
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Action Loading Backdrop */}
      <CBackDrop cOpen={actionLoading} />

      <Container maxWidth="md" sx={{ py: 0, px: { xs: 1, sm: 2 } }}>
        <Card
          elevation={3}
          sx={{
            overflow: "hidden",
            borderRadius: { xs: "16px", sm: "16px" },
            backgroundColor: "#fff",
          }}
        >
          {/* Header with gradient background */}
          <Box
            sx={{
              backgroundImage: "linear-gradient(120deg, #0d47a1, #0d47a1)",
              color: "white",
              padding: { xs: "1em", sm: "2em" },
              position: "relative",
            }}
          >
            <IconButton
              aria-label="back to home"
              onClick={handleHomeClick}
              sx={{
                position: "absolute",
                top: { xs: 8, sm: 16 },
                left: { xs: 8, sm: 16 },
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.3)",
                },
              }}
            >
              <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            
            <Box sx={{ textAlign: "center", pt: { xs: 1.5, sm: 0 } }}>
              <Typography
                variant={isMobile ? "h6" : "h4"}
                sx={{ 
                  fontWeight: "bold", 
                  letterSpacing: { xs: 0.5, sm: 1 },
                  textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
                  fontSize: { xs: "1.1rem", sm: "2rem" },
                }}
              >
                VISITOR APPROVAL
              </Typography>
              
              <Typography 
                variant={isMobile ? "body2" : "subtitle1"} 
                sx={{ 
                  mt: { xs: 0.5, sm: 1 }, 
                  opacity: 0.9,
                  fontWeight: 400,
                  letterSpacing: { xs: 0.2, sm: 0.5 },
                  fontSize: { xs: "0.8rem", sm: "1rem" },
                }}
              >
                Review and manage visitor request
              </Typography>
            </Box>
          </Box>

          {/* Status Section */}
          <Box 
            sx={{ 
              p: { xs: "1em", sm: "1.5em 2em" }, 
              backgroundColor: "#f8fafd",
              borderBottom: "1px solid #eaeff4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="body1"
              sx={{ 
                color: "#546e7a", 
                fontWeight: "500", 
                marginBottom: "0.5em",
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {showActionMessage ? "This visitor request is:" : "Current Status:"}
            </Typography>
            
            <StatusBadge status={status} />
            
            {showActionMessage && status !== "On Hold" && (
              <Typography
                variant="body2"
                sx={{ 
                  marginTop: "0.8em", 
                  color: "#78909c",
                  fontStyle: "italic",
                }}
              >
                No further actions are possible.
              </Typography>
            )}
          </Box>

          {/* Visitor Data Content */}
          <Box sx={{ p: { xs: "1em", sm: "1.5em 2em" } }}>
            {/* Mobile View - Stack */}
            {isMobile && (
              <Stack spacing={2} sx={{ mb: 2 }}>
                {visitorDataRows.map((row, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      border: "1px solid #eaeff4",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "rgba(25, 118, 210, 0.1)",
                          mr: 1,
                        }}
                      >
                        {row.icon}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", color: "#546e7a" }}
                      >
                        {row.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ ml: 5, color: "#263238", wordBreak: "break-word" }}
                    >
                      {row.value}
                    </Typography>
                  </Paper>
                ))}

                {/* Photo and Document in Mobile View - Updated with better spacing and security */}
                <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
                  <Grid container spacing={2} sx={{ justifyContent: "center" }}>
                    <Grid item xs={5.5}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: "8px",
                          border: "1px solid #eaeff4",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: { xs: "200px", sm: "250px" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexShrink: 0 }}>
                          <Avatar
                            sx={{
                              width: { xs: 24, sm: 28 },
                              height: { xs: 24, sm: 28 },
                              bgcolor: "rgba(25, 118, 210, 0.1)",
                              mr: 1,
                            }}
                          >
                            <PhotoIcon fontSize="small" color="primary" />
                          </Avatar>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontWeight: "bold", 
                              color: "#546e7a",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}
                          >
                            Photo
                          </Typography>
                        </Box>
                        
                        {visitorData.photo_url ? (
                          <Box 
                            sx={{ 
                              flex: 1, 
                              display: "flex", 
                              justifyContent: "center",
                              alignItems: "flex-start",
                              pt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: { xs: "140px", sm: "160px" },
                                position: "relative",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #e0e0e0",
                                cursor: "pointer",
                              }}
                              onClick={handleOpenPhotoPreview}
                            >
                              <img
                                src={visitorData.photo_url}
                                alt="Visitor Photo"
                                draggable={false}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "";
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                  WebkitUserDrag: "none",
                                  userDrag: "none",
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              flex: 1,
                              display: "flex", 
                              justifyContent: "center",
                              alignItems: "center", 
                              bgcolor: "#f5f5f5", 
                              borderRadius: "8px",
                              mt: 1,
                              height: { xs: "140px", sm: "160px" },
                            }}
                          >
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                              No Photo
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={5.5}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: "8px",
                          border: "1px solid #eaeff4",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: { xs: "200px", sm: "250px" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexShrink: 0 }}>
                          <Avatar
                            sx={{
                              width: { xs: 24, sm: 28 },
                              height: { xs: 24, sm: 28 },
                              bgcolor: "rgba(25, 118, 210, 0.1)",
                              mr: 1,
                            }}
                          >
                            <DescriptionIcon fontSize="small" color="primary" />
                          </Avatar>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontWeight: "bold", 
                              color: "#546e7a",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}
                          >
                            Document
                          </Typography>
                        </Box>
                        
                        {visitorData.document_url ? (
                          <Box 
                            sx={{ 
                              flex: 1, 
                              display: "flex", 
                              justifyContent: "center",
                              alignItems: "flex-start",
                              pt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: { xs: "140px", sm: "160px" },
                                position: "relative",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #e0e0e0",
                                cursor: "pointer",
                              }}
                              onClick={handleOpenDocumentPreview}
                            >
                              <img
                                src={visitorData.document_url}
                                alt="Visitor Document"
                                draggable={false}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "";
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                  WebkitUserDrag: "none",
                                  userDrag: "none",
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              flex: 1,
                              display: "flex", 
                              justifyContent: "center",
                              alignItems: "center", 
                              bgcolor: "#f5f5f5", 
                              borderRadius: "8px",
                              mt: 1,
                              height: { xs: "140px", sm: "160px" },
                            }}
                          >
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                              No Document
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            )}

            {/* Desktop/Tablet View - Table */}
            {!isMobile && (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ 
                  borderRadius: "12px", 
                  overflow: "hidden",
                  border: "1px solid #eaeff4",
                }}
              >
                <Table>
                  <TableBody>
                    {visitorDataRows.map((row, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          "&:nth-of-type(odd)": { 
                            backgroundColor: "#fafbfd" 
                          } 
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            width: "35%", 
                            pl: 3,
                            borderBottom: "1px solid #eaeff4",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {row.icon}
                            <Typography
                              variant="body1"
                              sx={{ 
                                fontWeight: "600", 
                                color: "#546e7a",
                                ml: 1.5,
                              }}
                            >
                              {row.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2,
                            borderBottom: "1px solid #eaeff4",
                          }}
                        >
                          <Typography 
                            variant="body1" 
                            sx={{ color: "#263238" }}
                          >
                            {row.value}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow 
                      sx={{ 
                        "&:nth-of-type(odd)": { 
                          backgroundColor: "#fafbfd" 
                        } 
                      }}
                    >
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PhotoIcon color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Photo
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        {visitorData.photo_url ? (
                          <Tooltip title="Click to preview">
                            <Box
                              sx={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #e0e0e0",
                                cursor: "pointer",
                              }}
                              onClick={handleOpenPhotoPreview}
                            >
                              <img
                                src={visitorData.photo_url}
                                alt="Visitor Photo"
                                draggable={false}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "";
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                  WebkitUserDrag: "none",
                                  userDrag: "none",
                                }}
                              />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No Photo Available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    <TableRow 
                      sx={{ 
                        "&:nth-of-type(odd)": { 
                          backgroundColor: "#fafbfd" 
                        } 
                      }}
                    >
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <DescriptionIcon color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Document
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        {visitorData.document_url ? (
                          <Tooltip title="Click to preview">
                            <Box
                              sx={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #e0e0e0",
                                cursor: "pointer",
                              }}
                              onClick={handleOpenDocumentPreview}
                            >
                              <img
                                src={visitorData.document_url}
                                alt="Visitor Document"
                                draggable={false}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "";
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none",
                                  WebkitUserDrag: "none",
                                  userDrag: "none",
                                }}
                              />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No Document Available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Action Buttons */}
            {showButtons && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  gap: { xs: 1, sm: 2 },
                  mt: { xs: 2, sm: 4 },
                  mb: { xs: 0.5, sm: 2 },
                  px: { xs: 0, sm: 0 },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleApprove}
                  disabled={disabled || actionLoading}
                  startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon />}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#2e7d32",
                    fontWeight: 600,
                    py: { xs: 1, sm: 1.2 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    flex: { xs: "1", sm: "0 1 auto" },
                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                    "&:disabled": {
                      backgroundColor: "#a5d6a7",
                      color: "#fff",
                    },
                    textTransform: "none",
                    boxShadow: "0 4px 8px rgba(46, 125, 50, 0.2)",
                  }}
                >
                  {actionLoading ? "Processing..." : "Approve"}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleReject}
                  disabled={disabled || actionLoading}
                  startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#d32f2f",
                    fontWeight: 600,
                    py: { xs: 1, sm: 1.2 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    flex: { xs: "1", sm: "0 1 auto" },
                    "&:hover": {
                      backgroundColor: "#b71c1c",
                    },
                    "&:disabled": {
                      backgroundColor: "#ef9a9a",
                      color: "#fff",
                    },
                    textTransform: "none",
                    boxShadow: "0 4px 8px rgba(211, 47, 47, 0.2)",
                  }}
                >
                  {actionLoading ? "Processing..." : "Reject"}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleOnHold}
                  disabled={disabled || actionLoading}
                  startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <HourglassEmptyIcon />}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#ed6c02",
                    fontWeight: 600,
                    color: "#fff",
                    py: { xs: 1, sm: 1.2 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    flex: { xs: "1", sm: "0 1 auto" },
                    "&:hover": {
                      backgroundColor: "#e65100",
                    },
                    "&:disabled": {
                      backgroundColor: "#ffcc02",
                      color: "#fff",
                    },
                    textTransform: "none",
                    boxShadow: "0 4px 8px rgba(237, 108, 2, 0.2)",
                  }}
                >
                  {actionLoading ? "Processing..." : "On Hold"}
                </Button>
              </Box>
            )}
          </Box>
        </Card>
      </Container>

      {/* Photo Preview Dialog - Enhanced with Security */}
      <Dialog 
        open={openPhotoPreview} 
        onClose={handleClosePhotoPreview}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "12px",
            overflow: "hidden",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h6">Visitor Photo</Typography>
          <IconButton
            onClick={handleClosePhotoPreview}
            sx={{ color: "white" }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          {photoPreview ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#000",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
              }}
            >
              <img
                src={photoPreview}
                alt="Visitor Photo Preview"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  maxWidth: "100%",
                  maxHeight: isMobile ? "85vh" : "70vh",
                  objectFit: "contain",
                  pointerEvents: "none",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  WebkitUserDrag: "none",
                  userDrag: "none",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <Typography variant="body1" color="textSecondary">
                Photo not available.
              </Typography>
            </Box>
          )}
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleClosePhotoPreview} 
              variant="contained"
              sx={{ borderRadius: "8px", textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Document Preview Dialog - Enhanced with Security */}
      <Dialog 
        open={openDocumentPreview} 
        onClose={handleCloseDocumentPreview}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "12px",
            overflow: "hidden",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h6">Document Preview</Typography>
          <IconButton
            onClick={handleCloseDocumentPreview}
            sx={{ color: "white" }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          {documentPreview ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#000",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
              }}
            >
              <img
                src={documentPreview}
                alt="Visitor Document Preview"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  maxWidth: "100%",
                  maxHeight: isMobile ? "85vh" : "70vh",
                  objectFit: "contain",
                  pointerEvents: "none",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  WebkitUserDrag: "none",
                  userDrag: "none",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <Typography variant="body1" color="textSecondary">
                Document not available.
              </Typography>
            </Box>
          )}
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDocumentPreview}
              variant="contained"
              sx={{ borderRadius: "8px", textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default VisitorApprovalView;